import { useEffect, useState, useCallback } from "react";
import { Eye, Ban, RotateCcw, Printer, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import ReceiptDialog from "../components/ReceiptDialog";
import { NeedTenant } from "../components/layout/AppShell";

const STATUS_STYLE = {
  PAID: "bg-emerald-100 text-emerald-700",
  VOID: "bg-red-100 text-red-700",
  REFUNDED: "bg-orange-100 text-orange-700",
};

export default function SalesPage() {
  const { user, activeTenantId } = useAuth();
  const [sales, setSales] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [action, setAction] = useState(null); // {type: 'void'|'refund', sale}
  const [reason, setReason] = useState("");
  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filters, setFilters] = useState({ from_date: "", to_date: "", invoice: "", customer: "", status: "", branch_id: "", cashier: "" });
  const canVoid = ["owner", "manager", "system_admin"].includes(user.role);

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/sales", { params });
      setSales(data);
    } catch (e) { toast.error(fmtErr(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
  }, [load, activeTenantId, user.role]);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
    if (canVoid) api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const submitAction = async () => {
    if (action.type !== "delete" && !reason.trim()) { toast.error("Reason is required"); return; }
    try {
      if (action.type === "delete") {
        await api.delete(`/sales/${action.sale.sale_id}`);
        toast.success(`Invoice ${action.sale.invoice_number} permanently deleted`);
      } else {
        await api.post(`/sales/${action.sale.sale_id}/${action.type}`, { reason });
        toast.success(`Invoice ${action.sale.invoice_number} ${action.type === "void" ? "voided" : "refunded"}`);
      }
      setAction(null); setReason("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reprint = async (s) => {
    try {
      const { data } = await api.post(`/sales/${s.sale_id}/reprint`);
      setViewing(data);
      toast.success("Reprint logged to audit trail");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  const setF = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="invoice-register-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Invoice Register</h1>
        <p className="text-sm text-slate-500">{sales.length} documents · controlled, immutable invoice sequence</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="invoice-filters">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" className="w-36 h-9" value={filters.from_date} onChange={setF("from_date")} data-testid="filter-from-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" className="w-36 h-9" value={filters.to_date} onChange={setF("to_date")} data-testid="filter-to-date" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Invoice no.</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input className="w-36 h-9 pl-8" placeholder="INV-000…" value={filters.invoice} onChange={setF("invoice")} data-testid="filter-invoice" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Customer</Label>
          <Input className="w-36 h-9" value={filters.customer} onChange={setF("customer")} data-testid="filter-customer" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger className="w-32 h-9" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="PAID">PAID</SelectItem>
              <SelectItem value="VOID">VOID</SelectItem>
              <SelectItem value="REFUNDED">REFUNDED</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {branches.length > 1 && (
          <div className="space-y-1">
            <Label className="text-xs">Branch</Label>
            <Select value={filters.branch_id} onValueChange={(v) => setFilters({ ...filters, branch_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-branch"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.branch_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {canVoid && staff.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs">Cashier</Label>
            <Select value={filters.cashier} onValueChange={(v) => setFilters({ ...filters, cashier: v === "all" ? "" : v })}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-cashier"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="invoice-register-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Invoice No.</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Cashier</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sales.map((s) => {
              const st = s.status || "PAID";
              return (
                <TableRow key={s.sale_id} data-testid={`invoice-row-${s.sale_id}`}>
                  <TableCell className="font-mono text-xs text-slate-700">{s.invoice_number}</TableCell>
                  <TableCell className="text-sm text-slate-600 whitespace-nowrap">{fmtDateTime(s.created_at)}</TableCell>
                  <TableCell className="text-sm text-slate-900">{s.buyer?.name || "Walk-in"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.cashier_name}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{peso(s.total_amount)}</TableCell>
                  <TableCell>
                    <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLE[st]}`} data-testid={`invoice-status-${s.sale_id}`}>{st}</span>
                    {s.void_reason && <p className="text-[10px] text-slate-400 mt-0.5">{s.void_reason}</p>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" title="View" onClick={() => setViewing(s)} data-testid={`view-invoice-${s.sale_id}`}>
                        <Eye className="h-4 w-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Reprint (audited)" onClick={() => reprint(s)} data-testid={`reprint-invoice-${s.sale_id}`}>
                        <Printer className="h-4 w-4 text-slate-500" />
                      </Button>
                      {canVoid && st === "PAID" && (
                        <>
                          <Button variant="ghost" size="icon" title="Void" onClick={() => setAction({ type: "void", sale: s })} data-testid={`void-invoice-${s.sale_id}`}>
                            <Ban className="h-4 w-4 text-red-500" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Refund" onClick={() => setAction({ type: "refund", sale: s })} data-testid={`refund-invoice-${s.sale_id}`}>
                            <RotateCcw className="h-4 w-4 text-orange-500" />
                          </Button>
                        </>
                      )}
                      {user.role === "system_admin" && (
                        <Button variant="ghost" size="icon" title="Delete permanently (admin)" onClick={() => setAction({ type: "delete", sale: s })} data-testid={`delete-invoice-${s.sale_id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {sales.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-slate-400">No invoices match the filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="max-w-xs" data-testid="void-refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading capitalize">{action?.type} {action?.sale.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {action?.type === "delete" ? (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 leading-relaxed">
                Permanent deletion (system admin only). The invoice is removed from the register and stock is restored. Normally you should void instead — deleted invoices leave a gap in the invoice sequence.
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                The invoice is never deleted — it stays in the register marked {action?.type === "void" ? "VOID" : "REFUNDED"}, stock is restored, and the action is written to the audit trail.
              </p>
            )}
            {action?.type !== "delete" && (
              <div className="space-y-1.5">
                <Label>Reason</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer cancellation" data-testid="void-reason-input" />
              </div>
            )}
            <Button className={`w-full ${action?.type === "refund" ? "bg-orange-600 hover:bg-orange-700" : "bg-red-600 hover:bg-red-700"}`} onClick={submitAction} data-testid="void-refund-confirm-btn">
              Confirm {action?.type}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReceiptDialog sale={viewing} open={!!viewing} onOpenChange={(o) => !o && setViewing(null)} />
    </div>
  );
}
